import { useCachedData } from '../hooks/useCachedData';
import { endpoints, api, clearCacheEntry } from '../api/config';
import { extractUserList } from '../utils/userUtils';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useMemo, useState, useCallback, useEffect } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Tooltip,
  Checkbox,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  OutlinedInput,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import { AVAILABLE_FEATURES, MOBILE_FEATURES, getDefaultFeatures, getFeaturesGroupedByCategory, getFeaturesByPlatform } from '../constants/features';

const Roles = () => {
  const { data: rolesData, isLoading, error, refetch } = useCachedData(endpoints.roles, {
    enabled: true,
  });

  // Fetch master admin details for web users (username + allocated_roles)
  const { data: adminData, isLoading: adminLoading } = useCachedData(endpoints.masterAdminDetails, {
    enabled: true,
  });

  // Fetch mobile users data (/wms-ai-users)
  const { data: mobileUsersData, isLoading: mobileUsersLoading } = useCachedData(endpoints.wmsAiUsers, {
    enabled: true,
  });

  // Check if user is super admin
  const isSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true';

  // Full customer list for the Item Sales Report customer-scope picker — fetched
  // once, unrestricted (this is the admin picker, not the report page itself).
  const [itemSalesCustomerOptions, setItemSalesCustomerOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(endpoints.itemSalesReportCustomers);
        const data = res.data?.customers;
        setItemSalesCustomerOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load item sales customer list:', err);
      }
    })();
  }, []);

  const normalizeFeaturePermissions = (raw, platform = 'web') => {
    const defaults = getDefaultFeatures(platform);
    if (!raw) return defaults;
    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        return defaults;
      }
    }
    if (typeof parsed === 'object' && parsed !== null) {
      return { ...defaults, ...parsed };
    }
    return defaults;
  };

  // Platform tab selector for the Roles page (default: 'web')
  const [selectedPlatform, setSelectedPlatform] = useState('web');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    role: '',
    platform: 'web',
    description: '',
    features: getDefaultFeatures('web'),
    customer_scopes: {},
    userids: [],
  });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [featureSearchQuery, setFeatureSearchQuery] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Assign users dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [roleToAssign, setRoleToAssign] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  // Users with roles dialog state
  const [usersWithRolesDialogOpen, setUsersWithRolesDialogOpen] = useState(false);

  // Force refetch on mount
  useEffect(() => {
    if (isSuperAdmin) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Step 1: Parse rolesData into platform buckets once, regardless of API shape.
  // New API: { web: [...], mobile: [...] }
  // Legacy API: { roles: [...] } or plain array (filtered by role.platform if present)
  const platformBuckets = useMemo(() => {
    const empty = { web: [], mobile: [] };
    if (!rolesData) return empty;

    // New API format: { web: [...], mobile: [...] }
    if (Array.isArray(rolesData.web) || Array.isArray(rolesData.mobile)) {
      return {
        web: Array.isArray(rolesData.web) ? rolesData.web : [],
        mobile: Array.isArray(rolesData.mobile) ? rolesData.mobile : [],
      };
    }

    // Legacy format: flat list — split by role.platform if present
    let allRoles = [];
    if (Array.isArray(rolesData.roles)) {
      allRoles = rolesData.roles;
    } else if (Array.isArray(rolesData)) {
      allRoles = rolesData;
    }

    return {
      web: allRoles.filter((r) => !r.platform || r.platform === 'web'),
      mobile: allRoles.filter((r) => r.platform === 'mobile'),
    };
  }, [rolesData]);

  // Step 2: Select the active bucket based on the platform tab.
  // selectedPlatform is the only dependency that changes on tab switch.
  const tableData = useMemo(() => {
    const roles = selectedPlatform === 'mobile' ? platformBuckets.mobile : platformBuckets.web;
    return roles.map((role) => ({
      ...role,
      featureCount: Object.values(role.feature_permissions || role.features || {}).filter((v) => v).length,
    }));
  }, [platformBuckets, selectedPlatform]);

  // Feature options are filtered based on the platform chosen in the dialog form
  const featureOptions = useMemo(
    () =>
      Object.entries(getFeaturesGroupedByCategory(form.platform)).flatMap(([category, features]) =>
        features.map(({ key, label }) => ({ key, label, category }))
      ),
    [form.platform]
  );

  const featureOptionsByKey = useMemo(() => {
    const map = new Map();
    featureOptions.forEach((f) => map.set(f.key, f));
    return map;
  }, [featureOptions]);

  const selectedFeatureOptions = useMemo(() => {
    if (Array.isArray(form.selectedFeatureKeys)) {
      return form.selectedFeatureKeys.map((key) => featureOptionsByKey.get(key)).filter(Boolean);
    }
    return featureOptions.filter((feature) => !!form.features[feature.key]);
  }, [featureOptions, featureOptionsByKey, form.selectedFeatureKeys, form.features]);

  const unassignedFeatureOptions = useMemo(() => {
    const assignedKeys = new Set(selectedFeatureOptions.map((f) => f.key));
    return featureOptions.filter((f) => !assignedKeys.has(f.key));
  }, [featureOptions, selectedFeatureOptions]);

  const filteredUnassignedOptions = useMemo(() => {
    if (!featureSearchQuery.trim()) return unassignedFeatureOptions;
    const q = featureSearchQuery.toLowerCase();
    return unassignedFeatureOptions.filter(
      (f) => f.label.toLowerCase().includes(q) || f.category.toLowerCase().includes(q)
    );
  }, [unassignedFeatureOptions, featureSearchQuery]);

  const handleAddFeature = useCallback((featureKey) => {
    setForm((prev) => {
      const currentKeys = prev.selectedFeatureKeys || [];
      if (currentKeys.includes(featureKey)) return prev;
      const nextKeys = [...currentKeys, featureKey];
      const nextFeatures = Object.fromEntries(
        Object.keys(getDefaultFeatures(prev.platform)).map((key) => [key, nextKeys.includes(key)])
      );
      return {
        ...prev,
        selectedFeatureKeys: nextKeys,
        features: nextFeatures,
      };
    });
  }, []);

  const handleRemoveFeature = useCallback((featureKey) => {
    setForm((prev) => {
      const currentKeys = prev.selectedFeatureKeys || [];
      const nextKeys = currentKeys.filter((k) => k !== featureKey);
      const nextFeatures = Object.fromEntries(
        Object.keys(getDefaultFeatures(prev.platform)).map((key) => [key, nextKeys.includes(key)])
      );
      return {
        ...prev,
        selectedFeatureKeys: nextKeys,
        features: nextFeatures,
      };
    });
  }, []);

  const handleOpenDialog = useCallback((role = null) => {
    if (role) {
      setEditingRoleId(role.role);
      const rolePlatform = role.platform || selectedPlatform;
      const normalizedFeatures = normalizeFeaturePermissions(
        role.feature_permissions || role.features,
        rolePlatform
      );
      const validPlatformKeys = new Set(Object.keys(getDefaultFeatures(rolePlatform)));
      const activeKeys = Object.keys(normalizedFeatures).filter(
        (k) => validPlatformKeys.has(k) && !!normalizedFeatures[k]
      );
      setForm({
        role: role.role || '',
        platform: rolePlatform,
        description: role.description || '',
        features: normalizedFeatures,
        selectedFeatureKeys: activeKeys,
        customer_scopes: role.customer_scopes || {},
        userids: role.userids || [],
        originalRoleObj: role,
      });
    } else {
      setEditingRoleId(null);
      setForm({
        role: '',
        platform: selectedPlatform,
        description: '',
        features: getDefaultFeatures(selectedPlatform),
        selectedFeatureKeys: [],
        customer_scopes: {},
        userids: [],
        originalRoleObj: null,
      });
    }
    setFormError('');
    setFeatureSearchQuery('');
    setOpen(true);
  }, [selectedPlatform]);

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingRoleId(null);
    setFeatureSearchQuery('');
    setForm({
      role: '',
      platform: selectedPlatform,
      description: '',
      features: getDefaultFeatures(selectedPlatform),
      selectedFeatureKeys: [],
      customer_scopes: {},
      userids: [],
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveRole = async () => {
    setFormError('');

    if (!form.role.trim()) {
      setFormError('Role name is required');
      return;
    }

    const hasFeatures = Object.values(form.features).some((v) => v);
    if (!hasFeatures) {
      setFormError('Please select at least one feature');
      return;
    }

    setLoading(true);

    try {
      // Filter to only include features that are true
      const enabledFeatures = Object.fromEntries(
        Object.entries(form.features).filter(([_, value]) => value === true)
      );
      // Only keep customer scopes for features that are actually enabled on this role
      const customerScopes = Object.fromEntries(
        Object.entries(form.customer_scopes || {}).filter(
          ([key, values]) => enabledFeatures[key] === true && Array.isArray(values) && values.length > 0
        )
      );

      const payload = {
        role: form.role,
        platform: form.platform,
        description: form.description,
        features: enabledFeatures,
        feature_permissions: enabledFeatures,
        customer_scopes: customerScopes,
        userids: form.userids || [],
        value: {
          role: form.role,
          platform: form.platform,
          description: form.description,
          features: enabledFeatures,
          feature_permissions: enabledFeatures,
          customer_scopes: customerScopes,
          userids: form.userids || [],
        },
      };

      if (editingRoleId) {
        // Update
        await api.put(`${endpoints.rolesUpdate}/${editingRoleId}`, payload);
        setSnackbar({
          open: true,
          message: 'Role updated successfully',
          severity: 'success',
        });
      } else {
        // Create
        await api.post(endpoints.rolesCreate, payload);
        setSnackbar({
          open: true,
          message: 'Role created successfully',
          severity: 'success',
        });
      }

      clearCacheEntry(endpoints.roles);
      refetch();
      handleCloseDialog();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to save role';
      setFormError(errorMsg);
      console.error('Error saving role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = useCallback((role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleOpenAssignDialog = useCallback(async (role) => {
    try {
      // Clear cache and refetch to get fresh data
      clearCacheEntry(endpoints.roles);
      const freshData = await refetch();

      // Find the fresh role data across both platforms (new API: { web, mobile })
      let freshRole = role;
      if (freshData?.data) {
        const raw = freshData.data;
        let allRoles = [];
        if (Array.isArray(raw.web) || Array.isArray(raw.mobile)) {
          allRoles = [...(raw.web || []), ...(raw.mobile || [])];
        } else if (Array.isArray(raw.roles)) {
          allRoles = raw.roles;
        } else if (Array.isArray(raw)) {
          allRoles = raw;
        }
        const foundRole = allRoles.find((r) => r.role === role.role);
        if (foundRole) {
          freshRole = foundRole;
        }
      }

      setRoleToAssign(freshRole);

      // Pre-select users whose allocated_roles includes this role name or who are listed in freshRole.userids
      const roleName = freshRole.role;
      const targetPlatform = freshRole.platform || selectedPlatform;

      let currentUsers = [];
      if (targetPlatform === 'mobile') {
        currentUsers = extractUserList(mobileUsersData);
      } else {
        currentUsers = extractUserList(adminData);
      }

      const roleUserIds = Array.isArray(freshRole.userids) ? freshRole.userids : [];

      const preSelected = currentUsers
        .filter((user) => {
          const uid = user.userid || user.username || user.name;
          if (!uid) return false;
          if (roleUserIds.includes(uid)) return true;
          return Array.isArray(user.allocated_roles) && user.allocated_roles.includes(roleName);
        })
        .map((user) => user.userid || user.username || user.name);

      setSelectedUserIds(preSelected);

      setAssignDialogOpen(true);
    } catch (err) {
      console.error('Error fetching fresh role data:', err);
      setRoleToAssign(role);
      const roleName = role.role;
      const targetPlatform = role.platform || selectedPlatform;

      let currentUsers = [];
      if (targetPlatform === 'mobile') {
        currentUsers = extractUserList(mobileUsersData);
      } else {
        currentUsers = extractUserList(adminData);
      }

      const roleUserIds = Array.isArray(role.userids) ? role.userids : [];

      const preSelected = currentUsers
        .filter((user) => {
          const uid = user.userid || user.username || user.name;
          if (!uid) return false;
          if (roleUserIds.includes(uid)) return true;
          return Array.isArray(user.allocated_roles) && user.allocated_roles.includes(roleName);
        })
        .map((user) => user.userid || user.username || user.name);

      setSelectedUserIds(preSelected);
      setAssignDialogOpen(true);
    }
  }, [adminData, mobileUsersData, selectedPlatform, refetch]);

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setRoleToAssign(null);
    setSelectedUserIds([]);
  };

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAssignRoleToUsers = async () => {
    if (!roleToAssign || selectedUserIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one user',
        severity: 'warning',
      });
      return;
    }

    setAssignLoading(true);
    try {
      await api.post('/roles/assign-users', {
        role: roleToAssign.role,
        userid: selectedUserIds,
        platform: selectedPlatform,
      });

      setSnackbar({
        open: true,
        message: `Role assigned to ${selectedUserIds.length} user(s)`,
        severity: 'success',
      });

      // Refresh data after assignment
      clearCacheEntry(endpoints.roles);
      clearCacheEntry(endpoints.masterAdminDetails);
      clearCacheEntry(endpoints.wmsAiUsers);
      await refetch();

      handleCloseAssignDialog();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to assign role';

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
      console.error('Error assigning role:', err);
    } finally {
      setAssignLoading(false);
    }
  };

  // Build user list from /wms-ai-users (for mobile platform) or /master-admin-details (for web platform)
  const usersList = useMemo(() => {
    if (selectedPlatform === 'mobile') {
      const mobileUsers = extractUserList(mobileUsersData);
      return mobileUsers
        .map((user) => {
          const uid = user.userid || user.username || user.name;
          return {
            username: uid,
            allocatedRoles: Array.isArray(user.allocated_roles) ? user.allocated_roles : [],
          };
        })
        .filter((u) => u.username)
        .sort((a, b) => a.username.localeCompare(b.username, undefined, { sensitivity: 'base' }));
    }

    const admins = extractUserList(adminData);
    return admins
      .map((admin) => ({
        username: admin.username || admin.userid || admin.name,
        allocatedRoles: Array.isArray(admin.allocated_roles) ? admin.allocated_roles : [],
      }))
      .filter((u) => u.username)
      .sort((a, b) => a.username.localeCompare(b.username, undefined, { sensitivity: 'base' }));
  }, [selectedPlatform, adminData, mobileUsersData]);

  const isAssignUsersLoading = selectedPlatform === 'mobile' ? mobileUsersLoading : adminLoading;

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    setDeleteLoading(true);
    try {
      const roleName = roleToDelete.role;
      await api.delete(`${endpoints.rolesDelete}/${roleName}`);

      setSnackbar({
        open: true,
        message: 'Role deleted successfully',
        severity: 'success',
      });

      clearCacheEntry(endpoints.roles);
      refetch();
      handleCloseDeleteDialog();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to delete role';

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
      console.error('Error deleting role:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownloadUsersWithRoles = () => {
    if (!tableData || tableData.length === 0) {
      setSnackbar({
        open: true,
        message: 'No roles available to download',
        severity: 'warning',
      });
      return;
    }

    const headers = ['Role Name', 'Description', 'Assigned Users'];
    const rows = tableData.map((role) => {
      const users = (role.userids || []).join(', ');
      // Escape quotes for CSV
      const escapeCsv = (str) => {
        if (!str) return '';
        const s = String(str);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      return [
        escapeCsv(role.role),
        escapeCsv(role.description),
        escapeCsv(users)
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `roles_and_users_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'role',
        header: 'Role Name',
        size: 150,
      },
      {
        accessorKey: 'featureCount',
        header: 'Features Assigned',
        size: 140,
        cell: (info) => {
          const assignedCount = info.getValue() ?? 0;
          const totalFeatures = Object.keys(getFeaturesByPlatform(selectedPlatform)).length;
          return `${assignedCount}/${totalFeatures}`;
        },
      },
      {
        id: 'assign_users',
        header: 'Assign',
        size: 82,
        minSize: 72,
        enableSorting: false,
        cell: (info) => (
          <Tooltip title="Assign Users">
            <IconButton
              size="small"
              onClick={() => handleOpenAssignDialog(info.row.original)}
              sx={{ color: 'success.main' }}
            >
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        id: 'edit_role',
        header: 'Edit',
        size: 72,
        minSize: 64,
        enableSorting: false,
        cell: (info) => (
          <Tooltip title="Edit Role">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(info.row.original)}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        id: 'delete_role',
        header: 'Delete',
        size: 82,
        minSize: 72,
        enableSorting: false,
        cell: (info) => (
          <Tooltip title="Delete Role">
            <IconButton
              size="small"
              onClick={() => handleOpenDeleteDialog(info.row.original)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [selectedPlatform, handleOpenAssignDialog, handleOpenDeleteDialog]
  );

  const getRowId = useCallback((row) => row.role || row.id || row.role_name, []);

  const rolesGrid = useDataGridProTable({
    title: 'Roles',
    data: tableData || [],
    columns,
    getRowId,
    loading: isLoading,
    error: null,
    disablePaper: true,
    hideHeader: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
  });

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            You do not have permission to access this page. Only super admin can manage roles.
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 2, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Header Section */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
          <Stack>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Role Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage roles and assign features to team members
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => setUsersWithRolesDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Users with Roles
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Create Role
            </Button>
          </Stack>
        </Stack>

        {/* Platform Selector */}
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <ToggleButtonGroup
            value={selectedPlatform}
            exclusive
            onChange={(_, value) => { if (value) setSelectedPlatform(value); }}
            size="small"
            aria-label="platform selector"
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1.5,
              p: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiToggleButton-root': {
                px: 2.5,
                py: 0.75,
                fontWeight: 500,
                color: 'text.primary',
                textTransform: 'none',
                border: 'none',
                borderRadius: '8px !important',
                transition: 'all 0.2s ease-in-out',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'common.white',
                  fontWeight: 700,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '&:not(.Mui-selected)': {
                  backgroundColor: 'transparent',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary',
                  },
                },
              },
            }}
          >
            <ToggleButton value="web" aria-label="web users">
              Web Users
            </ToggleButton>
            <ToggleButton value="mobile" aria-label="mobile users">
              Mobile Users
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || 'Failed to load roles'}
          </Alert>
        )}

        {/* Table Container */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MuiXDataGridSurface hook={rolesGrid} DataGridPro={DataGridPro} />
        </Paper>
      </Box>

      {/* Create/Edit Role Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingRoleId ? 'Edit Role' : 'Create New Role'}
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          {/* Top Section: Role Details */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 2,
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                autoFocus
                label="Role Name"
                name="role"
                value={form.role}
                onChange={handleFormChange}
                placeholder="e.g., Warehouse Manager"
                disabled={loading || !!editingRoleId}
                variant="outlined"
                size="small"
                sx={{ flex: 1, bgcolor: 'background.paper' }}
              />
              <FormControl size="small" disabled={loading} sx={{ width: 220, bgcolor: 'background.paper' }}>
                <InputLabel id="platform-select-label">Platform</InputLabel>
                <Select
                  labelId="platform-select-label"
                  id="platform-select"
                  value={form.platform}
                  label="Platform"
                  onChange={(e) => {
                    const newPlatform = e.target.value;
                    setForm((prev) => {
                      const rawRole = prev.originalRoleObj;
                      const normalized = normalizeFeaturePermissions(
                        rawRole ? (rawRole.feature_permissions || rawRole.features) : {},
                        newPlatform
                      );
                      const validNewKeys = new Set(Object.keys(getDefaultFeatures(newPlatform)));
                      const activeKeys = Object.keys(normalized).filter(
                        (k) => validNewKeys.has(k) && !!normalized[k]
                      );
                      return {
                        ...prev,
                        platform: newPlatform,
                        features: normalized,
                        selectedFeatureKeys: activeKeys,
                      };
                    });
                    setFeatureSearchQuery('');
                  }}
                >
                  <MenuItem value="web">Web Features</MenuItem>
                  <MenuItem value="mobile">Mobile Features</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Dual Panel Feature Manager */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            {/* Left Panel: Assigned Features */}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                height: 400,
                display: 'flex',
                flexDirection: 'column',
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Assigned Features ({selectedFeatureOptions.length})
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                  Selection order
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: 'background.paper' }}>
                {selectedFeatureOptions.length === 0 ? (
                  <Box sx={{ py: 8, px: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      No features assigned yet. Click "+ Add" on features from the right panel.
                    </Typography>
                  </Box>
                ) : (
                  selectedFeatureOptions.map((option) => (
                    <Paper
                      key={option.key}
                      elevation={0}
                      sx={{
                        mb: 1,
                        px: 1.75,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {option.label}
                      </Typography>
                      <Tooltip title="Remove feature">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFeature(option.key)}
                          disabled={loading}
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ))
                )}
              </Box>
            </Paper>

            {/* Right Panel: Available Features (Unassigned) */}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                height: 400,
                display: 'flex',
                flexDirection: 'column',
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: 'grey.100',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                  Available Features ({unassignedFeatureOptions.length})
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search available features..."
                  value={featureSearchQuery}
                  onChange={(e) => setFeatureSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={{ bgcolor: 'background.paper' }}
                />
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: 'background.paper' }}>
                {filteredUnassignedOptions.length === 0 ? (
                  <Box sx={{ py: 8, px: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {featureSearchQuery
                        ? 'No matching available features found.'
                        : 'All available features have been assigned!'}
                    </Typography>
                  </Box>
                ) : (
                  filteredUnassignedOptions.map((option) => (
                    <Paper
                      key={option.key}
                      elevation={0}
                      sx={{
                        mb: 1,
                        px: 1.75,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {option.label}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddFeature(option.key)}
                        disabled={loading}
                        sx={{ textTransform: 'none', py: 0.25, px: 1.5, fontSize: '0.75rem', fontWeight: 600, flexShrink: 0, ml: 1 }}
                      >
                        Add
                      </Button>
                    </Paper>
                  ))
                )}
              </Box>
            </Paper>
          </Box>

          {(form.selectedFeatureKeys || []).includes('itemSalesByCus') && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                Item Sales Report — Allowed Customers
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                Leave empty for unrestricted access (sees every customer). Pick one or more
                customers to lock this role to only their sales figures.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="role-item-sales-customers-label">Allowed customers</InputLabel>
                <Select
                  labelId="role-item-sales-customers-label"
                  multiple
                  value={form.customer_scopes?.itemSalesByCus || []}
                  onChange={(e) => {
                    const { value } = e.target;
                    const next = typeof value === 'string' ? value.split(',') : value;
                    setForm((prev) => {
                      const current = prev.customer_scopes?.itemSalesByCus || [];
                      if (next.includes('__select_all_customers__')) {
                        const everySelected =
                          itemSalesCustomerOptions.length > 0 &&
                          itemSalesCustomerOptions.every((c) => current.includes(c));
                        return {
                          ...prev,
                          customer_scopes: {
                            ...prev.customer_scopes,
                            itemSalesByCus: everySelected ? [] : [...itemSalesCustomerOptions],
                          },
                        };
                      }
                      return {
                        ...prev,
                        customer_scopes: {
                          ...prev.customer_scopes,
                          itemSalesByCus: next.filter((c) => c !== '__select_all_customers__'),
                        },
                      };
                    });
                  }}
                  input={<OutlinedInput label="Allowed customers" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'Unrestricted (all customers)';
                    if (
                      itemSalesCustomerOptions.length > 0 &&
                      selected.length === itemSalesCustomerOptions.length
                    ) {
                      return 'All customers';
                    }
                    return selected.join(', ');
                  }}
                  disabled={loading}
                >
                  {itemSalesCustomerOptions.length === 0 ? (
                    <MenuItem disabled>No customers available</MenuItem>
                  ) : (
                    [
                      <MenuItem
                        key="__select_all_customers__"
                        value="__select_all_customers__"
                        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Checkbox
                          checked={
                            itemSalesCustomerOptions.length > 0 &&
                            itemSalesCustomerOptions.every((c) =>
                              (form.customer_scopes?.itemSalesByCus || []).includes(c)
                            )
                          }
                          indeterminate={
                            (form.customer_scopes?.itemSalesByCus || []).length > 0 &&
                            (form.customer_scopes?.itemSalesByCus || []).length <
                              itemSalesCustomerOptions.length
                          }
                        />
                        <ListItemText primary="Select all" />
                      </MenuItem>,
                      ...itemSalesCustomerOptions.map((c) => (
                        <MenuItem key={c} value={c}>
                          <Checkbox checked={(form.customer_scopes?.itemSalesByCus || []).indexOf(c) > -1} />
                          <ListItemText primary={c} />
                        </MenuItem>
                      )),
                    ]
                  )}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRole}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {editingRoleId ? 'Update Role' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent dividers sx={{ minWidth: 400 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone
          </Alert>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete the role{' '}
            <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
              {roleToDelete?.role}
            </Typography>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRole}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users with Roles Dialog */}
      <Dialog open={usersWithRolesDialogOpen} onClose={() => setUsersWithRolesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Roles and Assigned Users
          <IconButton
            onClick={() => setUsersWithRolesDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : tableData.length === 0 ? (
            <Alert severity="info">No roles available</Alert>
          ) : (
            <Stack spacing={2}>
              {tableData.map((role) => (
                <Paper key={role.id} variant="outlined" sx={{ p: 2, backgroundColor: 'background.paper' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {role.role}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                    {role.description}
                  </Typography>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                      Assigned Users ({(role.userids || []).length}):
                    </Typography>
                    {(role.userids || []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        No users assigned
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(role.userids || []).map((userid) => (
                          <Typography
                            key={userid}
                            variant="body2"
                            sx={{
                              bgcolor: 'primary.lighter',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: 'action.hover',
                              border: '1px solid',
                              borderColor: 'primary.light',
                            }}
                          >
                            {userid}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDownloadUsersWithRoles}
            startIcon={<DownloadIcon />}
            variant="outlined"
            disabled={isLoading || tableData.length === 0}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            Download CSV
          </Button>
          <Button
            onClick={() => setUsersWithRolesDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Role to Users
          {roleToAssign && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              Role: <strong>{roleToAssign.role}</strong>
            </Typography>
          )}
          <IconButton
            onClick={handleCloseAssignDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isAssignUsersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : usersList.length === 0 ? (
            <Alert severity="info">No users available</Alert>
          ) : (
            <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List>
                {usersList.map((user) => (
                  <ListItem
                    key={user.username}
                    disablePadding
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={selectedUserIds.includes(user.username)}
                        onChange={() => handleUserToggle(user.username)}
                      />
                    }
                  >
                    <ListItemButton
                      onClick={() => handleUserToggle(user.username)}
                      dense
                    >
                      <ListItemText
                        primary={user.username}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {selectedUserIds.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseAssignDialog}
            disabled={assignLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignRoleToUsers}
            variant="contained"
            disabled={assignLoading || selectedUserIds.length === 0}
            startIcon={assignLoading ? <CircularProgress size={20} /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Assign Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Roles;
